import PropTypes from "prop-types";
import { connect } from "react-redux";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { addCaseNote, getCustomerDetailedHistory, getDetailedHistory } from "requests/homeowner/caseforms";
import MSectionCollection, { MSECTION_MODE_TAB } from "components/Shared/MSectionCollection";
import { 
  formatNoteDateCreated,
  formatNoteDateCreatedAllSection, 
  formatNoteDateUpdated, 
  getHistoryTypeDiv, 
  getNoteDetails, 
  getTabbedHistoryTable,
} from "components/Homeowner/History/DataFormatters";
import { CustomerServiceNotes } from "components/CustomerService";
import { hasCustomerRight } from "components/Shared/Functions";
import CaseFormDataRevisions from "components/Homeowner/CaseFormDataRevisions";
import {
  CASE_FORM_DATA_REVISIONS_VIEW,
  CASE_NOTE_MAINTENANCE,
  CASE_HISTORY_APPRAISER_NOTES_VIEW,
  CASE_HISTORY_APPRAISER_NOTES_EDIT,
} from "typeConstants/rights";
import CallLogs from "components/Homeowner/ContactEfforts/CallLogs.tsx";
import NoteInputField, { NOTE_EXTERNAL } from "components/Shared/NoteInputField";
import { CASE_NOTE_TYPE_GENERAL, CASE_NOTE_TYPE_APPRAISER } from "typeConstants/caseNoteTypes";

const TAB_ALL = "All";
const TAB_ALL_CUSTOMER = "All ";// the space at the end is a hack to seperate it from the TAB_ALL const
const TAB_CALL_LOG = "Call Logs";
const TAB_CS_NOTES = "CS Notes";
const TAB_ISSUES = "Issues";
const TAB_INTERNAL_NOTES = "Internal Notes";
const TAB_APPRAISER_NOTES = "Appraiser Notes";
const TAB_TIME_LOGS = "Time Logs";
const TAB_TOUCHES = "Touches";
const TAB_SYSTEM_TOUCHES = "System Touches";
const TAB_FORM_DATA_REVISIONS = "Form Data Revisions";
const TAB_EXTERNAL_NOTES = "External Notes";
const TAB_APPOINTMENT_CHANGES = "Appointments";

function TabbedHistory(props) {
  const [history, setHistory] = useState();

  let mounted = useRef(true);

  useEffect(() => {
    getHistory();
    return () => {
      mounted.current = false;
    };
  }, []);

  const getHistory = () => {
    // This is going to need to be refactored in the near future. It is dangerous to default to the full history detail and only display the modified version if they are a customer
    // The reason for not fixing it now is we don't have a full list of roles that use the full detailed history and a larger discussion/task is needed to discuss how rights are going to drive the display of components  
    hasCustomerRight(props.rights)
      ? getCustomerDetailedHistory(props.controlNumber, setHistory, mounted) :
      getDetailedHistory(props.controlNumber, setHistory, mounted);
  };

  const getData = section => {
    if (history) {
      switch (section) {
        case TAB_ALL:
          return history.all;
        case TAB_ALL_CUSTOMER:
          return history.all;
        case TAB_CALL_LOG:
          return history.callLogs;
        case TAB_CS_NOTES:
          return history.csNotes;
        case TAB_INTERNAL_NOTES:
          return history.internalNotes;
        case TAB_APPRAISER_NOTES:
          return history.appraiserNotes;
        case TAB_ISSUES:
          return history.issues;
        case TAB_TIME_LOGS:
          return history.timeLogs;
        case TAB_TOUCHES:
          return history.touches;
        case TAB_SYSTEM_TOUCHES:
          return history.systemTouches;
        case TAB_EXTERNAL_NOTES:
          return history.externalNotes;
        case TAB_APPOINTMENT_CHANGES:
          return history.appointmentChanges;
      }
    }
  };

  const getTableColDefs = section => {
    switch (section) {
      case TAB_ALL:
        return [
          { name: "Date", dataSource: formatNoteDateCreatedAllSection, style: { width: "200px" } },
          { name: "Type", dataSource: getHistoryTypeDiv, style: { width: "80px" } },
          { name: "Details", dataSource: getNoteDetails },
          { name: "Performed By", dataSource: "performedBy", style: { minWidth: "160px" } },
        ];
      case TAB_ALL_CUSTOMER:
        return [
          { name: "Date", dataSource: formatNoteDateCreated, style: { width: "200px" } },
          { name: "Type", dataSource: getHistoryTypeDiv, style: { width: "80px" } },
          { name: "Details", dataSource: getNoteDetails },
        ];
      case TAB_CALL_LOG:
        return [
          { name: "Date", dataSource: formatNoteDateCreated, style: { width: "200px" } },
          { name: "Contact", dataSource: "contact", style: { width: "250px" } },
          { name: "Details", dataSource: getNoteDetails },
          { name: "Notes", dataSource: "notes" },
        ];
      case TAB_CS_NOTES:
        return [
          { name: "Last Updated", dataSource: formatNoteDateUpdated, style: { width: "200px" } },
          { name: "Status", dataSource: "statusName" },
          { name: "Call Reason", dataSource: "callReason.name" },
          { name: "Details", dataSource: getNoteDetails, style: { minWidth: "300px" } },
          { name: "Contact", dataSource: "personType.name" },
          { name: "Department", dataSource: "assignedDepartment.name" },
          { name: "Assigned To", dataSource: "assignedTo" },
          { name: "Performed By", dataSource: "performedBy" },
        ];
      case TAB_INTERNAL_NOTES:
      case TAB_EXTERNAL_NOTES:
      case TAB_APPRAISER_NOTES:
        return [
          { name: "Date", dataSource: formatNoteDateCreated, style: { width: "200px" } },
          { name: "Details", dataSource: getNoteDetails },
          { name: "Performed By", dataSource: "performedBy" },
        ];
      case TAB_ISSUES:
        return [
          { name: "Date", dataSource: formatNoteDateCreated, style: { width: "200px" } },
          { name: "Details", dataSource: getNoteDetails },
        ];
      case TAB_TIME_LOGS:
        return [
          { name: "Date", dataSource: formatNoteDateCreated, style: { width: "200px" } },
          { name: "Details", dataSource: getNoteDetails },
          { name: "Performed By", dataSource: "performedBy" },
        ];
      case TAB_TOUCHES:
        return [
          { name: "Date", dataSource: formatNoteDateCreated, style: { width: "200px" } },
          { name: "Touch Type", dataSource: "touchType", style: { width: "300px" } },
          { name: "Details", dataSource: getNoteDetails },
          { name: "Performed By", dataSource: "performedBy" },
        ];
      case TAB_SYSTEM_TOUCHES:
        return [
          { name: "Date", dataSource: formatNoteDateCreated, style: { width: "200px" } },
          { name: "Touch Type", dataSource: "touchType", style: { width: "300px" } },
          { name: "Details", dataSource: getNoteDetails },
        ];
      case TAB_APPOINTMENT_CHANGES:
        return [
          { name: "Date", dataSource: formatNoteDateCreated, style: { width: "200px" } },
          { name: "Details", dataSource: getNoteDetails },
          { name: "Performed By", dataSource: "performedBy" },
        ];
    }
  };

  const saveGeneralNote = (noteText, noteType) => {
    let payload = {
      noteBody: noteText,
      isExternal: noteType === NOTE_EXTERNAL ? true : false
    };
    addCaseNote(props.controlNumber, CASE_NOTE_TYPE_GENERAL, payload, getHistory, mounted);
  };
  const saveAppraiserNote = (noteText) => {
    let payload = {
      noteBody: noteText,
      isExternal: false
    };
    addCaseNote(props.controlNumber, CASE_NOTE_TYPE_APPRAISER, payload, getHistory, mounted);
  };

  const getSection = section => {
    let colDefs = getTableColDefs(section);
    let data = getData(section);
    let tabControl;

    switch (section) {
      case TAB_CS_NOTES:
        tabControl = (
          <CustomerServiceNotes
            controlNumber={props.controlNumber}
            columnDefinitions={colDefs}
            tableData={data}
            refreshCallback={getHistory}
          />);
        break;
      case TAB_CALL_LOG:
        tabControl = (
          <CallLogs
            controlNumber={props.controlNumber}
            columnDefinitions={colDefs}
            tableData={data}
            refreshCallback={getHistory}
          />);
        break;
      case TAB_ALL:
      case TAB_INTERNAL_NOTES:
      case TAB_EXTERNAL_NOTES:
        tabControl = (
          <>
            {props.rights.some(r => r === CASE_NOTE_MAINTENANCE) && 
              <NoteInputField 
                saveNote={saveGeneralNote}
                placeholder="Add Note. Select “External” if you wish to allow the customer to see this note."
                showNoteType
              />}
            {getTabbedHistoryTable(colDefs, data, section, section == TAB_ALL ? undefined : "id")}
          </>);
        break;
      case TAB_APPRAISER_NOTES:
        tabControl = (
          <>
            {props.rights.some(r => r === CASE_HISTORY_APPRAISER_NOTES_EDIT) && 
              <NoteInputField 
                saveNote={saveAppraiserNote}
                placeholder="Add a note..."
              />}
            {props.rights.some(r => r === CASE_HISTORY_APPRAISER_NOTES_VIEW) && getTabbedHistoryTable(colDefs, data, section, section)}
          </>);
        break;
      case TAB_FORM_DATA_REVISIONS:
        tabControl = (
          <CaseFormDataRevisions controlNumber={props.controlNumber} />
        );
        break;
      default:
        tabControl = getTabbedHistoryTable(colDefs, data, section);
        break;
    }

    return (
      <div className="history-tab-section">
        {tabControl}
      </div>);
  };

  const sections = useMemo(
    () => {
      let sections = [];
      if (hasCustomerRight(props.rights)) {
        sections = [TAB_ALL_CUSTOMER, TAB_CALL_LOG, TAB_APPOINTMENT_CHANGES, TAB_CS_NOTES];
      } else {
        sections = [TAB_ALL];
        if (history?.callLogs) sections.push(TAB_CALL_LOG);
        if (history?.appointmentChanges) sections.push(TAB_APPOINTMENT_CHANGES);
        if (history?.csNotes) sections.push(TAB_CS_NOTES);
        if (history?.internalNotes) sections.push(TAB_INTERNAL_NOTES);
        if (history?.appraiserNotes) sections.push(TAB_APPRAISER_NOTES);
        if (history?.issues) sections.push(TAB_ISSUES);
        if (history?.timeLogs) sections.push(TAB_TIME_LOGS);
        if (history?.touches) sections.push(TAB_TOUCHES);
        if (history?.systemTouches) sections.push(TAB_SYSTEM_TOUCHES);

        if (props.rights.some(r => r === CASE_FORM_DATA_REVISIONS_VIEW)) {
          sections.splice(sections.indexOf(TAB_ALL) + 1, 0, TAB_FORM_DATA_REVISIONS);
        }
      }
      return sections.map(s => ({ title: s, children: getSection(s) }));
    },
    [props.roles, history]
  );

  return history ? (
    <MSectionCollection
      sections={sections}
      defaultMode={MSECTION_MODE_TAB}
      tabModeCollapsible={false}
      fullHeight
    />
  ) : <div />;
}


TabbedHistory.propTypes = {
  controlNumber: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  roles: PropTypes.array,
  rights: PropTypes.array,
};

const mapStateToProps = state => {
  return {
    roles: state.user.profile.roles,
    rights: state.user.profile.rights,
  };
};

export default connect(mapStateToProps)(TabbedHistory);